import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileCheck2, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, Badge } from "@/components/ui-kit";
import api from "@/lib/api";

const download = async (r) => {
  try {
    const res = await api.get(`/legal/acceptances/${r.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: `PranIQ_Clinician_Agreement_v${r.version}.pdf` });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error("Could not download the PDF"); }
};

function Marketing({ value, onChange }) {
  const toggle = async () => {
    try { const r = await api.post("/pro/legal/marketing", { opt_in: !value }); onChange(r.data.marketing_opt_in); toast.success(r.data.marketing_opt_in ? "Subscribed to product updates" : "Unsubscribed from promotional messages"); }
    catch { toast.error("Could not update preference"); }
  };
  return (
    <Card className="p-5" data-testid="pro-settings-marketing">
      <div className="flex items-center gap-2 font-semibold text-[14px]"><Mail size={16} className="text-teal-600" /> Communication preferences</div>
      <label className="mt-3 flex items-start gap-2.5 text-[13px] text-slate-700 cursor-pointer">
        <input type="checkbox" checked={value} onChange={toggle} className="mt-0.5 h-4 w-4 accent-teal-700" data-testid="pro-settings-marketing-toggle" />
        I agree to receive product updates and promotional messages from PranIQ. I can opt out at any time.
      </label>
      <p className="mt-2 text-[11.5px] text-slate-500">Service, safety and compliance messages are always sent.</p>
    </Card>
  );
}

export default function ProSettings() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const load = useCallback(() => {
    api.get("/pro/legal/acceptances").then((r) => setRows(r.data));
    api.get("/pro/legal/status").then((r) => setStatus(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="pro-settings">
      <PageHead title="Settings" subtitle="Your accepted agreements and communication preferences." action={<Link to="/pro"><Btn data-testid="pro-settings-back">Back to console</Btn></Link>} />
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card className="p-5" data-testid="pro-settings-agreements">
          <div className="flex items-center gap-2 font-semibold text-[14px]"><FileCheck2 size={16} className="text-teal-600" /> Agreements</div>
          <p className="mt-1 text-[12px] text-slate-500">Current version {status?.current_version || "—"} · <Link to="/legal/clinician/eula" target="_blank" className="text-teal-700 underline">EULA & Terms</Link> · <Link to="/legal/clinician/privacy" target="_blank" className="text-teal-700 underline">Privacy Policy</Link></p>
          <div className="mt-3 space-y-2">
            {rows.length === 0 && <p className="text-[12.5px] text-slate-500" data-testid="pro-settings-empty">No acceptance recorded yet.</p>}
            {rows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-slate-50 px-3 py-2.5 text-[12.5px]" data-testid={`pro-acceptance-${r.id}`}>
                <b className="text-slate-900">Version {r.version}</b>
                <Badge tone={r.kind === "signup" ? "teal" : "sky"}>{r.kind === "signup" ? "Sign-up" : "Re-acceptance"}</Badge>
                <span className="text-slate-600">{r.accepted_at_ist}</span>
                <span className="text-slate-400">· {r.device?.device_type} · {r.device?.browser}</span>
                <span className="text-slate-400 break-all">· hash {r.doc_hash.slice(0, 12)}…</span>
                <Btn size="sm" className="sm:ml-auto" onClick={() => download(r)} data-testid={`pro-acceptance-pdf-${r.id}`}><Download size={13} /> PDF</Btn>
              </div>
            ))}
          </div>
        </Card>
        {status && <Marketing value={status.marketing_opt_in} onChange={(v) => setStatus({ ...status, marketing_opt_in: v })} />}
      </div>
    </div>
  );
}
